import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Preguntas Frecuentes sobre Pladur | PLADUREX L\'Hospitalet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre instalación de pladur en L\'Hospitalet: plazos, precios, aislamiento acústico, pladur antihumedad, acabados y más. Respuestas de profesionales.');
    }
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const faqs = [
    {
      category: 'Proceso',
      question: '¿Cuánto tiempo tarda una instalación de pladur?',
      answer: 'Los plazos dependen del tipo y tamaño del proyecto. Un tabique divisorio sencillo puede estar terminado en 1-2 días. Un falso techo de una habitación puede llevar 2-3 días. Una reforma completa con varios tabiques, techos y trasdosados puede requerir entre 1 y 3 semanas. En la visita inicial te daremos un plazo exacto según tu proyecto específico.'
    },
    {
      category: 'Precio',
      question: '¿Qué factores influyen en el precio de una instalación de pladur?',
      answer: 'El precio depende de varios factores: metros cuadrados a instalar, altura de tabiques o techos, tipo de pladur (estándar, antihumedad, ignífugo), si es tabique simple o doble, tipo y grosor de aislamiento, complejidad de la instalación (curvas, arcos, diseños especiales), nivel de acabado requerido, y accesibilidad de la zona de trabajo. Siempre ofrecemos presupuestos detallados sin compromiso.'
    },
    {
      category: 'Técnico',
      question: '¿El pladur aísla del ruido? ¿Cuánto aislamiento acústico proporciona?',
      answer: 'Sí, el pladur puede proporcionar excelente aislamiento acústico cuando se instala correctamente. Un tabique simple con lana mineral puede reducir el ruido entre 35-40 dB. Un tabique doble con lana de roca y bandas resilientes puede alcanzar 50-60 dB de reducción. El nivel de aislamiento depende del grosor del tabique, tipo de aislante, número de placas y técnicas de instalación. Te asesoramos sobre la mejor solución según tus necesidades.'
    },
    {
      category: 'Técnico',
      question: '¿El pladur proporciona aislamiento térmico?',
      answer: 'Sí, especialmente cuando se instala con aislamiento térmico en su interior (lana mineral, poliestireno extruido, etc.). Los trasdosados con aislamiento pueden mejorar significativamente la eficiencia energética de paredes exteriores, reduciendo pérdidas de calor en invierno y entrada de calor en verano. Esto se traduce en mayor confort y ahorro en calefacción y aire acondicionado.'
    },
    {
      category: 'Técnico',
      question: '¿Se puede instalar pladur en baños, cocinas y zonas húmedas?',
      answer: 'Sí, para zonas húmedas utilizamos placas de pladur WA (antihumedad), que son de color verde y tienen tratamiento hidrófugo. Estas placas están diseñadas específicamente para resistir la humedad en baños, cocinas, lavaderos y cualquier zona con vapor de agua. Son perfectas como base para alicatar y ofrecen la misma resistencia que las placas estándar pero con protección adicional contra la humedad.'
    },
    {
      category: 'Proceso',
      question: '¿Los acabados de pladur quedan listos para pintar?',
      answer: 'Sí, entregamos las superficies de pladur completamente lisas y preparadas para pintar directamente. Realizamos el tratamiento de juntas con cinta y pasta, lijado fino y aplicación de imprimación si se solicita. El acabado es perfectamente liso y uniforme, listo para aplicar la pintura o el revestimiento que prefieras.'
    },
    {
      category: 'Proceso',
      question: '¿Hacen mucho ruido y suciedad las obras de pladur?',
      answer: 'La instalación de pladur es considerablemente más limpia que la obra tradicional con ladrillo y cemento. Genera menos escombros y polvo. Aun así, hay cortes de perfiles metálicos y placas que producen algo de ruido y polvo. Trabajamos con aspiradores industriales, protegemos muebles y suelos con lonas, y limpiamos diariamente. Si vives en la vivienda durante la obra, coordinamos horarios para minimizar molestias.'
    },
    {
      category: 'Precio',
      question: '¿Emiten factura por los trabajos?',
      answer: 'Sí, emitimos factura detallada de todos nuestros trabajos. Trabajamos de forma completamente legal y transparente. La factura incluye el desglose de materiales y mano de obra, y cumple con todas las normativas fiscales vigentes.'
    },
    {
      category: 'Técnico',
      question: '¿Se pueden colgar muebles pesados en paredes de pladur?',
      answer: 'Sí, se pueden colgar muebles, estanterías, televisores y otros elementos en pladur utilizando los anclajes adecuados. Para cargas ligeras (cuadros, espejos pequeños) se usan tacos especiales para pladur. Para cargas medias y pesadas (muebles de cocina, TV, estanterías) se instalan refuerzos de madera o perfiles metálicos en el interior del tabique durante la instalación. Si nos indicas qué necesitas colgar, lo preparamos durante la instalación.'
    },
    {
      category: 'Proceso',
      question: '¿Necesito permisos o licencias para instalar pladur?',
      answer: 'Para instalaciones interiores de pladur en viviendas particulares (tabiques, techos, trasdosados) generalmente no se requiere licencia de obras, ya que se consideran obras menores que no afectan a la estructura del edificio. Sin embargo, si la reforma es integral o afecta a elementos comunes, puede ser necesario comunicarlo a la comunidad de vecinos. En locales comerciales puede requerirse licencia según el ayuntamiento. Te asesoramos en cada caso específico.'
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary/10 to-primary-light/10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0421aadd2a1463afda1633a5d8de8b1b.jpg"
            alt="Preguntas Frecuentes"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/80 to-white/85"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-primary cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary font-semibold">FAQ</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-text-dark mb-6">
              Preguntas Frecuentes sobre <strong>Pladur</strong>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Resolvemos tus dudas sobre instalación de pladur en L'Hospitalet de Llobregat
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-text-dark">
              Todas las preguntas
            </h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-start justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 pr-4">
                    <div className="flex items-start">
                      <i className="ri-question-line text-primary text-2xl mr-3 mt-1 flex-shrink-0"></i>
                      <div>
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-2 ${
                          faq.category === 'Precio' ? 'bg-blue-100 text-blue-700' :
                          faq.category === 'Proceso' ? 'bg-green-100 text-green-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {faq.category}
                        </span>
                        <h3 className="text-lg md:text-xl font-bold text-text-dark">
                          {faq.question}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <motion.i
                    className={`ri-arrow-down-s-line text-2xl text-primary flex-shrink-0 mt-1`}
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  ></motion.i>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pl-16">
                        <p className="text-gray-600 leading-relaxed text-base">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - No encuentras tu respuesta */}
      <section className="py-16 bg-bg-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="bg-primary/10 border-2 border-primary rounded-2xl p-8 text-center"
            {...fadeInUp}
          >
            <i className="ri-question-answer-line text-5xl text-primary mb-4"></i>
            <h2 className="text-3xl font-bold text-text-dark mb-4">
              ¿No encuentras tu respuesta?
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Contacta con nosotros y resolveremos todas tus dudas sobre instalación de pladur
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-bold text-lg rounded-lg hover:bg-primary-dark transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3 text-xl"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#25D366] text-white font-bold text-lg rounded-lg hover:bg-[#20BA5A] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-3 text-xl"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enlaces Internos */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" {...fadeInUp}>
            <h2 className="text-3xl font-bold text-text-dark mb-4">
              Más Información
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/servicios"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-service-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Nuestros Servicios
              </h3>
              <p className="text-sm text-gray-600">
                Descubre todos los servicios de pladur que ofrecemos
              </p>
            </Link>
            <Link
              to="/empresa"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-building-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Sobre Nosotros
              </h3>
              <p className="text-sm text-gray-600">
                Conoce cómo trabajamos y nuestro compromiso
              </p>
            </Link>
            <Link
              to="/contacto"
              className="bg-bg-light rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-mail-send-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Contacto
              </h3>
              <p className="text-sm text-gray-600">
                Solicita tu presupuesto sin compromiso
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <CookieBanner />

      {/* JSON-LD FAQPage Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
    </>
  );
};

export default FaqPage;
